import {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
} from 'react';
import { cx } from '@parrot/utils';
import styles from './Tabs.module.css';

type TabsContextValue = {
  value: string;
  onValueChange: (value: string) => void;
};

const TabsContext = createContext<TabsContextValue | null>(null);

const useTabsContext = (component: string) => {
  const context = useContext(TabsContext);
  if (context === null) throw new Error(`<${component}> must be rendered inside <Tabs>.`);
  return context;
};

/* defaultValue is omitted from the div attributes rather than left to pass through:
   reaching for it is the natural mistake here, and a type error is a better answer
   than it silently landing on the DOM. */
type TabsProps = Omit<HTMLAttributes<HTMLDivElement>, 'defaultValue'> & {
  /** The selected tab. Pass '' for no selection. */
  value: string;
  onValueChange: (value: string) => void;
};

/**
 * A set of panels sharing one region, one visible at a time.
 *
 * Four flat exports — Tabs, TabList, Tab, TabPanel — talking over context, so the
 * markup between them is yours: a TabList can sit in a page header with the panels
 * far below it.
 *
 * Strictly controlled: it holds no state, so a click is a request and nothing moves
 * until the value comes back down. That's what lets a wizard decline a jump ahead,
 * and it means there's only ever one copy of the selection to reason about.
 *
 * These are plain buttons, deliberately. Tab semantics promise arrow-key navigation,
 * and shipping the roles without the keys is worse than shipping neither.
 */
export const Tabs = ({ value, onValueChange, className, ...divProps }: TabsProps) => (
  <TabsContext.Provider value={{ value, onValueChange }}>
    <div className={cx(styles.base, className)} {...divProps} />
  </TabsContext.Provider>
);

type TabListProps = HTMLAttributes<HTMLDivElement>;

export const TabList = ({ className, ...divProps }: TabListProps) => {
  const { value } = useTabsContext('TabList');
  const listRef = useRef<HTMLDivElement>(null);

  // Layout, not plain, effect: setting the offsets before the first paint stops the
  // indicator animating in from zero width.
  useLayoutEffect(() => {
    const list = listRef.current;
    if (list === null) return;

    const measure = () => {
      const tab = list.querySelector<HTMLElement>(':scope > [data-active]');
      list.style.setProperty('--tab-indicator-left', `${tab?.offsetLeft ?? 0}px`);
      list.style.setProperty('--tab-indicator-width', `${tab?.offsetWidth ?? 0}px`);
    };

    measure();

    // Tab widths move after that first measure: --font-sans is Axiforma, and every
    // label reflows when it swaps in. Watching the tabs rather than the list, because
    // the list is as wide as its parent whatever the labels do.
    // jsdom has no ResizeObserver, and apps consume this library from source.
    const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : undefined;
    for (const tab of list.children) resize?.observe(tab);

    // A tab added or removed moves the others without resizing any of them.
    const mutation =
      typeof MutationObserver === 'function'
        ? new MutationObserver(() => {
            for (const tab of list.children) resize?.observe(tab);
            measure();
          })
        : undefined;
    mutation?.observe(list, { childList: true });

    return () => {
      resize?.disconnect();
      mutation?.disconnect();
    };
  }, [value]);

  return <div ref={listRef} className={cx(styles.list, className)} {...divProps} />;
};

type TabProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'value' | 'type'> & {
  /** Ties this tab to its TabPanel. */
  value: string;
  /** A number beside the label — matching rows, unread items. */
  count?: number;
};

export const Tab = ({ value, count, className, children, onClick, ...buttonProps }: TabProps) => {
  const context = useTabsContext('Tab');
  const isActive = context.value === value;

  return (
    <button
      type={'button'}
      data-active={isActive || undefined}
      className={cx(styles.tab, isActive && styles.tabActive, className)}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) context.onValueChange(value);
      }}
      {...buttonProps}
    >
      {children}
      {count !== undefined && <span className={styles.count}>{count}</span>}
    </button>
  );
};

type TabPanelProps = HTMLAttributes<HTMLDivElement> & {
  /** Matches the Tab that reveals this panel. */
  value: string;
  /** Keep the panel in the DOM while another tab shows, hidden instead of unmounted. */
  forceMount?: boolean;
};

export const TabPanel = ({ value, forceMount = false, className, ...divProps }: TabPanelProps) => {
  const context = useTabsContext('TabPanel');
  const isActive = context.value === value;

  if (!isActive && !forceMount) return null;

  return <div hidden={!isActive} className={cx(styles.panel, className)} {...divProps} />;
};
